import * as React from "react"
import Container from "../container"

import img_1 from "../../images/amare_img_1_h.jpg"
import img_malharia from "../../images/pemalex_img_malharia_3_h.jpg"
import img_estamparia from "../../images/pemalex_img_estamparia_2_h.jpg"
import img_entretelas from "../../images/pemalex_img_fabrica_h.jpg"

import * as produtosStyles from "./produtos.module.css"

/*
 * Ficha técnica — option A of the ficha-técnica handoff: the three product
 * lines as full-colour bands that are always on the page and open in place.
 * It is the original accordion's language, with the contents of each line laid
 * out as a spec table instead of a list of headings.
 *
 * `key` is the line's name and does double duty as the id its band carries —
 * #malharia, #estamparia, #entretelas — which the lead paragraph deep-links
 * to. It used to be a separate `anchor` field reading first-/second-/
 * third-section, which said only where a band sat rather than what was in it,
 * and went stale the moment one moved.
 *
 * `tone` is the class holding the line's colour, and `columns` exists because
 * the mobile layout drops the <thead> and prints each column name back in
 * front of its value — the handoff writes that from the renderer and notes a
 * component implementation should pass the labels as props instead.
 */
const LINES = [
  {
    key: "malharia",
    tone: produtosStyles.malharia,
    num: "01",
    name: "Malharia",
    meta: "4 produtos",
    image: img_malharia,
    alt: "Teares circulares de malharia",
    columns: ["Produto", "Composição", "Aplicação"],
    rows: [
      ["Malha de poliamida", "Poliamida + elastano", "Moda íntima, praia e fitness"],
      ["Malha de poliéster", "Poliéster + elastano", "Bojos para moda íntima e praia"],
      ["Malha de viscose", "Viscose + elastano", "Moda feminina"],
      ["Malha de algodão", "Algodão + elastano", "Moda feminina e casual"],
    ],
    note:
      "Gramaturas e larguras sob consulta. Produção em teares circulares " +
      "Pilotelli, com acabamento em ramas Ewhwa e HT Fong's.",
  },
  {
    key: "estamparia",
    tone: produtosStyles.estamparia,
    num: "02",
    name: "Estamparia",
    meta: "3 processos",
    image: img_estamparia,
    alt: "Impressão digital em tecido",
    columns: ["Processo", "Bases", "Aplicação"],
    rows: [
      ["Impressão digital", "Viscose, linho, algodão", "Tecidos planos e malhas"],
      ["Sublimação", "Poliéster", "Moda praia, fitness e esportiva"],
      ["Estampa exclusiva", "Sob consulta", "Desenvolvimento sob medida por cliente"],
    ],
    note:
      "Impressão sob demanda: sem mínimos de estoque, sem excedente. " +
      "Desenvolvimento de estampa pelo estúdio Amaré Design.",
  },
  {
    key: "entretelas",
    tone: produtosStyles.entretelas,
    num: "03",
    name: "Entretelas",
    meta: "3 produtos",
    image: img_entretelas,
    alt: "Galpão de entretelas",
    columns: ["Produto", "Estrutura", "Aplicação"],
    rows: [
      ["Entretela de malha", "Malha resinada", "Confecção feminina — caimento e maciez"],
      ["Entretela não-tecida", "Não-tecido fusionável", "Forma, estrutura e conforto"],
      ["Colarinhos", "Fusionado", "Camisaria"],
    ],
    note:
      "Por muitos anos a maior fornecedora de entretelas da América Latina, " +
      "com produção integralmente local.",
  },
]

/* Malharia starts open, per the handoff — on a tablet and up. See
   MOBILE_CLOSED below for why a phone starts with all three shut. */
const INITIAL_OPEN = LINES.map((line, i) => i === 0)

const ALL_CLOSED = LINES.map(() => false)

/* The width the rest of the site treats as a phone. An open Malharia costs a
   phone a full screen of scrolling before the second line is even in reach, so
   the three headings are the whole list there and the reader picks one. */
const MOBILE_CLOSED = "(max-width: 767.98px)"

/* `useLayoutEffect` so the correction lands before the browser paints the
   hydrated tree rather than a frame after it. It has no meaning during SSR —
   where the markup is the tablet default — so the server gets the passive one
   and React stays quiet about it. */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect

/*
 * The same reveal preset the Amaré section, the home band and the Sobre
 * timeline use, with the handoff's 80ms stagger. It sits on the bands, which
 * are always mounted — opening one only toggles a class inside it.
 */
const reveal = (delay = 0) => ({
  "data-sal": "slide-up",
  "data-sal-delay": delay,
  "data-sal-easing": "ease-out-circ",
  "data-sal-duration": 1200,
})

/*
 * Produtos — the third stop on the one-pager, and what /product/ still
 * renders.
 */
export default function Produtos() {
  const [open, setOpen] = React.useState(INITIAL_OPEN)
  const bandRefs = React.useRef([])

  /*
   * Phones start with every line closed. Read once on mount rather than from a
   * matchMedia listener: re-running it on resize would reopen — or shut — a
   * band the reader had just chosen for themselves, and rotating a phone into
   * landscape is exactly the case that would trigger it.
   *
   * A deep link is left alone. Arriving on /#estamparia means the reader has
   * already named the line they want, and the effect below opens it.
   */
  useIsomorphicLayoutEffect(() => {
    if (window.location.hash) return
    if (!window.matchMedia(MOBILE_CLOSED).matches) return
    setOpen(ALL_CLOSED)
  }, [])

  /*
   * Deep links into a line: open that one alone and bring it into view. The
   * scroll is done here rather than left to the browser because the bands
   * above it change height as they close, which would leave a plain anchor
   * jump pointing at the wrong offset.
   */
  React.useEffect(() => {
    const openFromHash = () => {
      const i = LINES.findIndex(line => "#" + line.key === window.location.hash)
      if (i < 0) return
      setOpen(LINES.map((line, idx) => idx === i))
      window.requestAnimationFrame(() => {
        if (bandRefs.current[i]) bandRefs.current[i].scrollIntoView()
      })
    }
    openFromHash()
    window.addEventListener("hashchange", openFromHash)
    return () => window.removeEventListener("hashchange", openFromHash)
  }, [])

  /* Independent toggles — more than one line can be open at a time, so the
     specifications of two lines can be read side by side. */
  const toggle = index => {
    setOpen(current => current.map((isOpen, i) => (i === index ? !isOpen : isOpen)))
  }

  return (
    <section id="produtos" className={produtosStyles.section}>
      <Container>
        <h2 className={produtosStyles.title}>Nossos Produtos</h2>
        <h3 className="pt-4">
          A Pemalex se destaca por sua ampla gama de produtos de alta qualidade, que abrange <a className={produtosStyles.link} href="#malharia">malhas</a>, <a className={produtosStyles.link} href="#estamparia">tecidos estampados</a>, e <a className={produtosStyles.link} href="#entretelas">entretelas</a>.
        </h3>
        <div className={produtosStyles.wrapper}>
          <p>
            As nossas malhas produzidas em poliamida e poliéster oferecem durabilidade, elasticidade e conforto, tornando-as ideais para uma variedade de aplicações, desde roupas esportivas até moda íntima. Além disso, as entretelas são essenciais para dar estrutura e suporte às peças de vestuário, garantindo um acabamento impecável.
          </p>
          <p>
            Os nossos tecidos estampados, criados pelo nosso estúdio de design interno, <em>Amaré Design</em>, são feitos à mão e digitalizados, incorporando padrões exclusivos que atendem às tendências da moda e aos gostos mais exigentes dos nossos clientes.
          </p>
        </div>
        <img data-sal="slide-left" data-sal-delay="100" data-sal-easing="ease-out-circ" data-sal-duration="1900" src={img_1} alt="Manequim" className={produtosStyles.leadImage} />
        <h3 className={produtosStyles.paragraph}>Juntos, esses produtos representam a excelência e a versatilidade da Pemalex, que se empenha em oferecer soluções de alta qualidade para a indústria da moda, trazendo tecnologias de última gerações, como a máquina digital <em>Reggiani</em>, tear circulares <em>Pilotelli</em>, HT <em>Fong's</em>, e ramas <em>Ewhwa</em>.</h3>
      </Container>
      {/* The bands run edge to edge like the section they replace, so only the
          intro sits in a Container. Inside a band, `.headInner` / `.bodyInner`
          re-align the contents with the page's content column — the colour
          bleeds, the copy still lines up with everything above it. */}
      <div>
        <Container>
          <h3 className={produtosStyles.fichaHead}>Ficha técnica</h3>
          <p className={produtosStyles.fichaLead}>
            Toque numa linha para abrir composições, aplicações e bases
            disponíveis.
          </p>
        </Container>

        {/* Open state is a data attribute, and the band's `className` MUST
            stay constant. sal (gatsby-plugin-scroll-reveal) reveals a
            [data-sal] element by adding `sal-animate` to it imperatively, and
            it runs with `once: true`. React rewrites the whole class attribute
            whenever the className prop changes, which would strip that class
            off and drop the band back to opacity 0 with no observer left to
            put it back — the band would vanish on its first click. Anything
            conditional on this element belongs in an attribute, never in the
            class list. */}
        <div className={produtosStyles.stack}>
          {LINES.map((line, i) => (
            <article
              key={line.key}
              id={line.key}
              ref={el => (bandRefs.current[i] = el)}
              className={`${produtosStyles.band} ${line.tone}`}
              data-open={open[i] ? "true" : "false"}
              {...reveal(i * 80)}
            >
              <button
                type="button"
                className={produtosStyles.bandHead}
                aria-expanded={open[i]}
                aria-controls={`ficha-${line.key}`}
                onClick={() => toggle(i)}
              >
                <span className={produtosStyles.headInner}>
                  <span className={produtosStyles.num}>{line.num}</span>
                  <span className={produtosStyles.name}>{line.name}</span>
                  <span className={produtosStyles.meta}>{line.meta}</span>
                  <span className={produtosStyles.sign} aria-hidden="true">
                    {open[i] ? "×" : "+"}
                  </span>
                </span>
              </button>

              <div className={produtosStyles.bandBody} id={`ficha-${line.key}`}>
                <div className={produtosStyles.bodyInner}>
                  <figure className={produtosStyles.bandMedia}>
                    <img src={line.image} alt={line.alt} />
                  </figure>
                  <div className={produtosStyles.bandData}>
                    <table className={produtosStyles.specT}>
                      <thead>
                        <tr>
                          {line.columns.map(column => (
                            <th key={column} scope="col">
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {line.rows.map(cells => (
                          <tr key={cells[0]}>
                            {cells.map((cell, c) => (
                              <td key={line.columns[c]} data-label={line.columns[c]}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className={produtosStyles.note}>{line.note}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      <div className={produtosStyles.parallax}></div>
    </section>
  )
}
